/**
 * Session storage utilities for cookie-based authentication
 * API uses cookies for session management, we only store user data locally for quick access
 */

const SESSION_DATA_KEY = 'mira_session_data';

/**
 * Persona data from API session
 */
export interface PersonaData {
  entity: string;
  first_name: string;
  last_name: string;
  state: number;
}

/**
 * Session data structure matching API response from GET /v1/session
 */
export interface SessionData {
  authenticated: boolean;
  session: {
    account: number;
    persona: PersonaData;
    role: string;
  };
}

/**
 * User data derived from session for easier access in components
 */
export interface UserData {
  account: number;
  entity: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: string;
  state: number;
}

/**
 * Store session data in localStorage for quick access
 * Note: Actual authentication is cookie-based, managed by the API
 */
export function setSession(data: SessionData): void {
  try {
    localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to store session data:', error);
  }
}

/**
 * Get session data from localStorage
 */
export function getSession(): SessionData | null {
  try {
    const data = localStorage.getItem(SESSION_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to get session data:', error);
    return null;
  }
}

/**
 * Get user data from session
 */
export function getUserData(): UserData | null {
  try {
    const session = getSession();
    if (!session || !session.authenticated) {
      return null;
    }

    const { persona, account, role } = session.session;
    
    return {
      account,
      entity: persona.entity,
      firstName: persona.first_name.trim(), // Trim whitespace
      lastName: persona.last_name.trim(),   // Trim whitespace (API has leading space!)
      fullName: `${persona.first_name.trim()} ${persona.last_name.trim()}`,
      role,
      state: persona.state,
    };
  } catch (error) {
    console.error('Failed to get user data:', error);
    return null;
  }
}

/**
 * Clear session data from localStorage
 * Note: Cookie is invalidated by calling DELETE /v1/session on the API
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_DATA_KEY);
    // Clean up old keys if they exist
    localStorage.removeItem('mira_access_token');
    localStorage.removeItem('mira_refresh_token');
    localStorage.removeItem('mira_user_data');
  } catch (error) {
    console.error('Failed to clear session data:', error);
  }
}

/**
 * Check if a valid session exists locally
 * Note: This checks localStorage only, actual validation is cookie-based
 */
export function hasSession(): boolean {
  const session = getSession();
  return !!session && session.authenticated;
}
